<template>
  <div>
    <div class="detail-filter-row">
      <FilterItem v-for="(filter, rank) in filterSetup?.filter(f => !f.isAction)"
                  :key="filter.key"
                  :filter="filter"
                  :rank="rank"
                  @click="onShowModalFilter"
                  @reset="resetFilter" />

      <!-- Action Buttons -->
      <BButton  v-for="filter in filterSetup?.filter(f => f.isAction && showActions)"
                :key="filter.key"
                variant="secondary"
                size="sm"
                :class="{ 'filter-action-icon-only': filter.iconOnly }"
                @click="onFilterAction(filter)">
        <component  v-if="filter.icon"
                    :is="filter.icon"
                    :size="14" />
        <span v-if="!filter.iconOnly" class="ms-1">{{ filter.label }}</span>
      </BButton>

      <FilterInfos  v-if="showFilterInfos"
                    :filter-groups="getActiveFilters"
                    @remove-tag="handleRemoveTag"
                    class="my-2" />

      <ModalFilter  v-model="currentFilter.showModal"
                    :initial-data="currentFilter.options"
                    :cached-options="currentFilter.cachedOptions"
                    :show-all-options="currentFilter.showAllOptions"
                    :filter-name="currentFilter.key"
                    :placeholder="currentFilter.placeholder"
                    @apply="onChangeModalFilter" />
    </div>
  </div>
</template>

<script>
import { Circle, Filter, ChevronDown, Globe,
  RefreshCw, MapPin, Layers, Funnel,
  SquareDashed, Square, Blocks, Building2,
  PackageOpen,Package, X } from 'lucide-vue-next';
import { mapGetters, mapActions } from 'vuex';
import ModalFilter from '@/componentsv3/ModalFilter.vue';
import FilterInfos from '@/componentsv3/FilterInfos.vue';
import FilterItem from '@/componentsv3/FilterItem.vue';
import { countCheckedLeaves } from '@/utils/treeHelpers';
import DashboardService from '@/services/DashboardService-APP_TARGET';

export default {
  name: 'DetailFilterRow',
  emits: ['filter-changed', 'filter-click'],

  props: {
    showActions:        { type: Boolean, default: true },
    initialSelection:   { type: Object, default: null },
    disableOptions:     { type: Array, default: () => [] },
    disableFilterButton: { type: Boolean, default: false },
  },

  components: {
    Filter,
    FilterItem,
    FilterInfos,
    ModalFilter,
    ChevronDown,
    RefreshCw, Globe, MapPin, Layers, Funnel,
    SquareDashed, Square, Blocks, Building2, PackageOpen, Package, X
  },

  computed: {
    ...mapGetters('auth', [ 'clientId', 'isMSMUser' ]),
    ...mapGetters('clientData', [ 'brandIds', 'countryCodes', 'getFiltersCopy' ]),
    ...mapGetters('localization', [ 'currentLocale' ]),

    // Only UI
    getActiveFilters() {
      let result = [];
      if (!this.filterSetup || !this.showActions)
        return result;

      const filterFields = this.filterSetup.filter(x => !x.isAction);
      filterFields.forEach(filter => {
        let options = filter.customIndex !== -1 ? this.filters.customProperties[filter.customIndex] : this.filters[filter.key];
        const count = countCheckedLeaves(options);
        if (count > 0) {
          let tags = [];
          const gatherTags = (node) => {
            // Wenn node ein Array ist, iteriere darüber
            if (Array.isArray(node)) {
              node.forEach(item => gatherTags(item));
              return;
            }
            // Wenn node children hat, ist es kein Leaf - rekursiv durchgehen
            if (node.children && Array.isArray(node.children) && node.children.length > 0) {
              node.children.forEach(child => gatherTags(child));
            } else {
              // Kein children = Leaf node
              if (node.checked === true) {
                tags.push({
                  id: node.id || node.key || node.value,
                  label: (node.label || node.name || node.value).replace('...', '').replace('…', '').replace(/s$/, ''),
                  icon: node.icon || null
                });
              }
            }
          };
          gatherTags(options);
          result.push({
            key: filter.key,
            label: filter.label.replace('...', '').replace('…', '').replace(/s$/, ''),
            tags: tags,
            divider: false
          });
        }
      });
      return result;
    },

    filterDefinitions() {
      const items = APP_TARGET === 'bmw' ? this.defaultFiltersBMW : this.defaultFilters;
      return items.filter(f => !f.isAction);
    }
  },

  watch: {
    getFiltersCopy(newVal, oldVal) {
      if (newVal) {
        this.filters = this.getFiltersCopy;
        this.filterSetup = this.setupFilterSettings(this.filters);
        this.initializeSelectedFilter();
      }
    }
  },

  data() {
    return {
      filterSetup: undefined,
      showModalFilter: false,
      filters: null,

      // Alle selektierten Filterwerte (wird dynamisch initialisiert)
      selection: {},

      showFilterInfos: this.disableFilterButton ? false : true,

      // der Filter der aktuell im Modal angezeigt und angepasst werden kann vom User
      currentFilter: {
        showModal: false,
        options: [],
        cachedOptions: null,
        showAllOptions: false,
        key: null,
        placeholder: '',
      },

      defaultFilters: [
        { key: 'projects',  icon: Funnel,     placeholder: 'Webportal.StructureFilter.Placeholder' },
        { key: 'scenarios', icon: Layers,     placeholder: 'Webportal.ScenarioFilter.Placeholder' },
        { key: 'waves',     icon: Globe,      placeholder: 'Webportal.WaveFilter.Placeholder' },
        { key: 'region',    icon: Square,     placeholder: 'Webportal.RegionFilter.Placeholder' },
        { key: 'outlet',    icon: MapPin,     placeholder: 'Webportal.ClientLocationFilter.Placeholder' },
        { key: 'locations', icon: MapPin,     placeholder: 'Webportal.LocationFilter.Placeholder' },
        { key: 'refresh',   icon: RefreshCw,  label: this.$t('Webportal.Refresh'), isAction: true },
        { key: 'reset',     icon: X,          isAction: true, iconOnly: true },
        { key: 'fold',      icon: ChevronDown,isAction: true, iconOnly: true }
      ],

      defaultFiltersBMW: [
        { key: 'Brand',           icon: Funnel,       placeholder: 'Webportal.BrandFilter.Placeholder' },
        { key: 'Surveys',         icon: Layers,       placeholder: 'Webportal.ScenarioFilter.Placeholder' }, //Webportal.SurveyFilter.Placeholder' },
        { key: 'Waves',           icon: Globe,        placeholder: 'Webportal.WaveFilter.Placeholder' },
        { key: 'Regions',         icon: Square,       placeholder: 'Webportal.RegionFilter.Placeholder' },
        { key: 'Subregions',      icon: Blocks,       placeholder: 'Webportal.SubregionFilter.Placeholder' },
        { key: 'NSCs',            icon: Package,      placeholder: 'Webportal.DashboardBMW.NSCFilter.Placeholder' },
        { key: 'SubNSCs',         icon: PackageOpen,  placeholder: 'Webportal.DashboardBMW.NSCSubregionFilter.Placeholder' },
        { key: 'Areas',           icon: SquareDashed, placeholder: 'Webportal.AreaFilter.Placeholder' },
        { key: 'Dealergroups',    icon: Building2,    placeholder: 'Webportal.DashboardBMW.DealergroupFilter.Placeholder' },
        { key: 'ClientLocations', icon: MapPin,       placeholder: 'Webportal.DashboardBMW.OutletFilter.Placeholder' },
        // { key: 'WebsiteAnswers',  icon: Globe,        placeholder: 'Webportal.DashboardBMW.ContactInitiationFilter.Placeholder' },
        { key: 'refresh',         icon: RefreshCw,    label: this.$t('Webportal.Refresh'), isAction: true },
        { key: 'reset',           icon: X,            isAction: true, iconOnly: true },
        { key: 'fold',            icon: ChevronDown,  isAction: true, iconOnly: true }
      ],
    };
  },

  mounted() {
    this.filters = this.getFiltersCopy;
    this.filterSetup = this.setupFilterSettings(this.filters);
    this.initializeSelectedFilter();
  },

  methods: {
    ...mapActions('clientData', [ 'setAppLoading' ]),

    applyInitialSelection() {
      if (!this.initialSelection || !this.filters)
        return;

      Object.keys(this.initialSelection).forEach(filterKey => {
        const selectedIds = this.initialSelection[filterKey];
        if (!selectedIds || !Array.isArray(selectedIds) || selectedIds.length === 0)
          return;

        const filterData = this.filters[filterKey];
        if (!filterData)
          return;

        // Setze checked auf false für alle Nodes
        const resetChecked = (nodes) => {
          if (!nodes) return;
          if (!Array.isArray(nodes)) nodes = [nodes];
          nodes.forEach(node => {
            node.checked = false;
            if (node.children && Array.isArray(node.children)) {
              resetChecked(node.children);
            }
          });
        };

        // Setze checked auf true für selected IDs
        const applySelection = (nodes) => {
          if (!nodes) return;
          if (!Array.isArray(nodes)) nodes = [nodes];

          nodes.forEach(node => {
            if (node.children && Array.isArray(node.children) && node.children.length > 0) {
              applySelection(node.children);
            } else {
              // Leaf node - prüfe ob ID in selectedIds
              let id = node.id?.toString();
              // Entferne Präfixe wie 'B', 'S' etc.
              if (id && id.match(/^[A-Z]\d+/)) {
                id = id.substring(1);
              }
              if (id && selectedIds.includes(id)) {
                node.checked = true;
              }
            }
          });
        };

        resetChecked(filterData);
        applySelection(filterData);
      });
    },

    initializeSelectedFilter() {
      if (!this.filters)
        return;

      // Wende initialSelection an, falls vorhanden
      this.applyInitialSelection();

      const dfltFilters = this.filterDefinitions;
      dfltFilters.forEach(dflt => {
        if (this.filters[dflt.key]) {
          try {
            this.selection[dflt.key] = this.extractSelectedIds(dflt.key);
          } catch (error) {
            log.error("SEL FLT: ", this.selection);
            this.selection[dflt.key] = [];
          }
        }
      });
    },

    extractSelectedIds(filterKey) {
      const filterData = this.filters[filterKey];
      if (!filterData) return [];

      const selectedIds = [];
      const traverse = (nodes) => {
        if (!nodes) return;
        if (!Array.isArray(nodes)) nodes = [nodes];

        nodes.forEach(node => {
          if (node.children && Array.isArray(node.children) && node.children.length > 0) {
            // Hat Kinder, rekursiv durchgehen
            traverse(node.children);
          } else {
            // Leaf node - prüfe checked Status
            if (node.checked === true) {
              // Entferne Präfixe wie 'B', 'S', etc. und extrahiere die reine ID
              let id = node.id?.toString();
              if (id && id.match(/^[A-Z]\d+/)) {
                id = id.substring(1); // Entferne ersten Buchstaben
              }
              if (id) {
                selectedIds.push(id);
              }
            }
          }
        });
      };

      try {
        traverse(filterData);
      } catch (error) {
        log.error('Fehler beim Extrahieren der ausgewählten IDs für Filter', filterKey, ':', error);
      }
      return selectedIds;
    },

    async reloadFiltersFromApi() {
      let params = { api: '2021', "localization-id": this.currentLocale, ...this.selection };
      try {
        this.setAppLoading(true);
        const response = await DashboardService.postOptions(params, null);
        this.filters = response?.data || this.filters;
      } catch (error) {
        log.error('Fehler beim Laden der Filter:', error);
      } finally {
        this.setAppLoading(false);
      }
    },

    raiseRefreshData() {
      this.$emit('filter-changed', this.currentFilter, this.filters);
    },

    handleRemoveTag({ groupKey, tag }) {
      const filterData = this.filters[groupKey];
      if (!filterData) return;

      // Finde das Blatt mit tag.label und setze checked auf false
      const uncheckNode = (nodes) => {
        if (!nodes) return false;
        if (!Array.isArray(nodes)) nodes = [nodes];

        for (const node of nodes) {
          if (node.children && Array.isArray(node.children) && node.children.length > 0) {
            // Hat Kinder, rekursiv durchgehen
            if (uncheckNode(node.children)) return true;
          } else {
            // Leaf node - prüfe ob Label übereinstimmt
            const nodeLabel = (node.label || node.name || node.value || '').replace('...', '').replace('…', '').replace(/s$/, '');
            if (nodeLabel === tag.label && node.checked === true) {
              node.checked = false;
              return true;
            }
          }
        }
        return false;
      };

      uncheckNode(filterData);

      // Aktualisiere selection und lade Filter neu
      this.selection[groupKey] = this.extractSelectedIds(groupKey);
      this.reloadFiltersFromApi();
    },

    // reset filter at rank and all below
    resetFilter(rank) {
      const fltrs = this.filterSetup.filter(f => !f.isAction);
      for (let i = rank; i <= fltrs.length; i++) {
        const filterKey = fltrs[i].key;
        this.selection[filterKey] = [];
      }
      this.reloadFiltersFromApi();
    },

    onFilterAction(filter) {
      switch (filter.key) {
        case 'refresh':
          this.reloadFiltersFromApi();
          break;
        case 'fold':
          this.showFilterInfos = !this.showFilterInfos;
          break;
        case 'reset':
          // Reset all filters
          Object.keys(this.filters).forEach(key => {
            if (Array.isArray(this.filters[key])) {
              const resetArray = (arr) => {
                arr.forEach(item => {
                  if (item.children && Array.isArray(item.children)) {
                    resetArray(item.children);
                  } else {
                    item.checked = false;
                  }
                });
              };
              resetArray(this.filters[key]);
            }
          });
          // Erzwinge Reaktivität durch neue Referenz
          this.filters = { ...this.filters };
          this.raiseRefreshData();
          break;
        default:
          log.error('Unknown filter action:', filter.key);
          break;
      }
    },

    onChangeModalFilter(name, items, selected) {
      this.selection[name] = selected;
      this.reloadFiltersFromApi();
      this.$emit('filter-changed', this.currentFilter, this.filters);
    },

    onShowModalFilter(filter) {
      const geographicFilterKeys = ['Regions', 'Subregions', 'NSCs', 'SubNSCs', 'Areas'];
      let options = filter.customIndex !== -1 ? this.filters.customProperties[filter.customIndex] : this.filters[filter.key];

      let cachedOptions = null;
      if (geographicFilterKeys.includes(filter.key) && this.getFiltersCopy?.[filter.key]) {
        const raw = this.getFiltersCopy[filter.key];
        cachedOptions = Array.isArray(raw) ? raw : [raw];
      }

      this.currentFilter = {
        options: Array.isArray(options) ? options : [options],
        cachedOptions,
        showAllOptions: cachedOptions !== null,
        key: filter.key,
        placeholder: filter.label,
        showModal: true
      };
    },

    getFilterText(filter) {
      const count = countCheckedLeaves(this.selection[filter.key]);
      if (count === 0)
        return `${filter.label}`;
      else
        return `${filter.label} (${count})`;
    },

    // Once for init of filter buttons and actions
    setupFilterSettings(rawFilters) {
      const settings = [];
      if (rawFilters === null || rawFilters === undefined)
        return settings;

      const dfltFilters = APP_TARGET === 'bmw' ? this.defaultFiltersBMW : this.defaultFilters;
      dfltFilters.forEach(dflt => {
        if (Object.hasOwn(rawFilters, dflt.key)) {
          const filter = rawFilters[dflt.key];
          if (this.disableOptions.includes(dflt.key)){
            return;
          }
          settings.push({
            key: dflt.key,
            customIndex: -1,
            label: this.$t(dflt.placeholder), // filter.selectedOption ? filter.selectedOption.label : filter.label,
            icon: dflt.icon || filter.icon || null,
            isAction: filter.isAction || false
          });
          return;
        }

        rawFilters.customProperties?.forEach((item) => {
          if (dflt.key === item.label.toLowerCase()) {
            settings.push({
              key: dflt.key,
              customIndex: rawFilters.customProperties.indexOf(item),
              label: dflt.placeholder ? this.$t(dflt.placeholder) : item.label,
              icon: dflt.icon || filter.icon || null,
              isAction: item.isAction || false
            });
          }
        });
      });
      settings.push(...dfltFilters.filter(x => x.isAction && x.key !== 'refresh' && x.key !== 'reset'));
      return settings;
    },
  }
};
</script>

<style lang="scss" >
.detail-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  width: 100%;
}

// BButton im Filter-Row Context
.detail-filter-row .btn {
  height: 1.925rem;
  display: flow;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

// Action Button Styles
.filter-action-icon-only {
  min-width: 2rem !important;
  width: 2rem;
  padding: 0.25rem !important;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
}

</style>
